import { css, StyleSheet } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { useGetAllLevAdminGroups } from '../../../queries/Admin/UserOperations/useGetAllLevAdminGroups';
import { type ISupportMailbox, useUpdateSupportMailboxMutation } from '../../../queries/Support/useSupportMailboxQuery';
import { getApiErrorMessage } from '../../../utils/Support/supportHelpers';
import { ConfirmModal } from '../../components/supportShared/ConfirmModal';
import { Modal } from '../../../web/components/Modal';
import { TrashIcon } from '../../../web/components/svgs/icons/TrashIcon';
import { Button } from '../../../web/components-v2/Button';
import { Checkbox } from '../../../web/components-v2/Checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../web/components-v2/Select';
import { THEME } from '../../../web/components-v2/Theme/theme';
import { styleSheet } from '../../components/accountDetails/ManageInternalRolesModal/styles';

// Local, so the stylesheet shared with ManageInternalRolesModal stays untouched.
const s = StyleSheet.create({
	sectionHeading: {
		marginBottom: 8,
		marginTop: 0,
	},
	roleItem: {
		padding: '8px 12px',
	},
	indent: {
		paddingLeft: 12,
		paddingRight: 12,
	},
	removeButton: {
		paddingRight: 0,
	},
	// No left inset: the trigger's own 12px padding lands its text on the role names above.
	addRow: {
		marginTop: 8,
	},
	visibilityRow: {
		alignItems: 'flex-start',
		display: 'flex',
		gap: 10,
	},
	visibilityLabel: {
		cursor: 'pointer',
		fontSize: 14,
		fontWeight: 600,
	},
	visibilityDescription: {
		color: THEME.colors.text.light,
		display: 'block',
		fontSize: 13,
		fontWeight: 400,
		marginTop: 4,
	},
});

const getFriendlyRoleName = (role: string) => (role.startsWith('Levitate:') ? role.slice('Levitate:'.length) : role);

const getFullRoleName = (role: string) => (role.includes(':') ? role : `Levitate:${role}`);

const getComparableRoleName = (role: string) => getFullRoleName(role).toLowerCase();

export function ManageMailboxPermissionsModal({ mailbox, onClose }: { mailbox: ISupportMailbox; onClose: () => void }) {
	const [currentRoles, setCurrentRoles] = React.useState(mailbox.allowedRoles ?? []);
	const [isRestricted, setIsRestricted] = React.useState(mailbox.isRestricted ?? false);
	const [isConfirmingUnrestrict, setIsConfirmingUnrestrict] = React.useState(false);
	const { data: allRoles, isLoading, isError } = useGetAllLevAdminGroups();
	const updateMailboxMutation = useUpdateSupportMailboxMutation(mailbox.id);

	React.useEffect(() => {
		setCurrentRoles(mailbox.allowedRoles ?? []);
	}, [mailbox.allowedRoles]);

	React.useEffect(() => {
		setIsRestricted(mailbox.isRestricted ?? false);
	}, [mailbox.isRestricted]);

	const saveRestricted = async (nextRestricted: boolean) => {
		// Optimistic so the checkbox doesn't lag the round trip; reverted below if the save fails.
		setIsRestricted(nextRestricted);
		try {
			await updateMailboxMutation.mutateAsync({ isRestricted: nextRestricted });
			toast.success(nextRestricted ? 'Mailbox restricted' : 'Mailbox visible to everyone');
		} catch (error: unknown) {
			setIsRestricted(!nextRestricted);
			toast.error(`Failed to update mailbox visibility: ${getApiErrorMessage(error, 'Unknown error')}`);
		}
	};

	// Removing the restriction exposes every past ticket and can't be undone by re-checking the box.
	const handleRestrictedChange = (nextRestricted: boolean) => {
		if (nextRestricted) {
			return saveRestricted(true);
		}
		setIsConfirmingUnrestrict(true);
	};

	const currentRoleNames = new Set(currentRoles.map(getComparableRoleName));
	const availableRolesToAdd = (allRoles ?? []).filter(role => !currentRoleNames.has(getComparableRoleName(role)));

	// The trigger only ever shows the placeholder, so size it to the widest option instead.
	const widestRoleLabel = availableRolesToAdd.reduce((widest, role) => {
		const label = getFriendlyRoleName(role);
		return label.length > widest.length ? label : widest;
	}, '');

	const handleAddRole = async (role: string) => {
		if (!role) return;

		const fullRoleName = getFullRoleName(role);
		const nextRoles = [...currentRoles.map(getFullRoleName), fullRoleName];
		try {
			await updateMailboxMutation.mutateAsync({ allowedRoles: nextRoles });
			setCurrentRoles(nextRoles);
			toast.success(`Mailbox permission "${getFriendlyRoleName(fullRoleName)}" added successfully`);
		} catch (error: unknown) {
			toast.error(`Failed to add mailbox permission: ${getApiErrorMessage(error, 'Unknown error')}`);
		}
	};

	const handleRemoveRole = async (roleName: string) => {
		const comparableRoleName = getComparableRoleName(roleName);
		const nextRoles = currentRoles
			.filter(role => getComparableRoleName(role) !== comparableRoleName)
			.map(getFullRoleName);
		try {
			await updateMailboxMutation.mutateAsync({ allowedRoles: nextRoles });
			setCurrentRoles(nextRoles);
			toast.success(`Mailbox permission "${getFriendlyRoleName(roleName)}" removed successfully`);
		} catch (error: unknown) {
			toast.error(`Failed to remove mailbox permission: ${getApiErrorMessage(error, 'Unknown error')}`);
		}
	};

	return (
		<Modal isOpen>
			<article className={css(styleSheet.modalContainer)}>
				<h3>Manage Mailbox Permissions for {mailbox.name}</h3>

				{isLoading ? (
					<div className={css(styleSheet.loadingContainer)}>
						<p>Loading permissions...</p>
					</div>
				) : (
					<>
						<div className={css(styleSheet.section)}>
							<h4 className={css(s.sectionHeading)}>Visibility</h4>
							<div className={css(s.visibilityRow)}>
								<Checkbox
									id='mailbox-restricted'
									checked={isRestricted}
									onCheckedChange={checked => handleRestrictedChange(checked === true)}
									disabled={updateMailboxMutation.isPending}
								/>
								<label htmlFor='mailbox-restricted' className={css(s.visibilityLabel)}>
									Restrict this mailbox
									<span className={css(s.visibilityDescription)}>
										{isRestricted
											? 'Restricted — only this mailbox’s teammates and the roles below can open or search its tickets.'
											: 'Not restricted — anyone at Levitate can open or search this mailbox’s tickets.'}
									</span>
								</label>
							</div>
						</div>

						<div className={css(styleSheet.section)}>
							<h4 className={css(s.sectionHeading)}>Roles with access</h4>
							{currentRoles.length === 0 ? (
								<p className={css(styleSheet.emptyMessage, s.indent)}>No roles have access</p>
							) : (
								<ul className={css(styleSheet.rolesList)}>
									{currentRoles.map(role => (
										<li key={role} className={css(styleSheet.roleItem, s.roleItem)}>
											<span className={css(styleSheet.roleName)}>{getFriendlyRoleName(role)}</span>
											<Button
												type='button'
												variant='ghost'
												size='icon'
												onClick={() => handleRemoveRole(role)}
												disabled={updateMailboxMutation.isPending}
												styles={[styleSheet.removeButton, s.removeButton]}
												title={`Remove ${getFriendlyRoleName(role)}`}
												aria-label={`Remove ${getFriendlyRoleName(role)}`}
											>
												<TrashIcon fillColor={THEME.colors.danger.main} />
											</Button>
										</li>
									))}
								</ul>
							)}
							{isError ? (
								<p className={css(styleSheet.emptyMessage, s.indent, s.addRow)}>Failed to load available roles</p>
							) : availableRolesToAdd.length === 0 ? (
								<p className={css(styleSheet.emptyMessage, s.indent, s.addRow)}>All roles already have access</p>
							) : (
								<div className={css(styleSheet.addRoleContainer, s.addRow)}>
									<Select value='' onValueChange={handleAddRole} disabled={updateMailboxMutation.isPending}>
										<SelectTrigger style={{ minWidth: `calc(${widestRoleLabel.length}ch + 4rem)` }}>
											<SelectValue placeholder='Add a role...' />
										</SelectTrigger>
										<SelectContent>
											{availableRolesToAdd.map(role => (
												<SelectItem key={role} value={role}>
													{getFriendlyRoleName(role)}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
							)}
						</div>
					</>
				)}

				<div className={css(styleSheet.modalActions)}>
					<Button onClick={onClose} disabled={updateMailboxMutation.isPending} variant='secondary'>
						Close
					</Button>
				</div>
			</article>

			{isConfirmingUnrestrict && (
				<ConfirmModal
					title='Remove restriction'
					message={`Anyone at Levitate will be able to read every ticket in "${mailbox.name}", including past ones. Restricting it again later won’t undo that.`}
					confirmLabel={updateMailboxMutation.isPending ? 'Removing...' : 'Remove restriction'}
					variant='danger'
					onConfirm={() => {
						setIsConfirmingUnrestrict(false);
						void saveRestricted(false);
					}}
					onCancel={() => setIsConfirmingUnrestrict(false)}
				/>
			)}
		</Modal>
	);
}
